export const WELCOME_ROUTE = "/welcome-display";
export const WELCOME_WINDOW_NAME = "dcWelcome";

const CHANNEL_NAME = "dc-welcome";
const STORAGE_KEY = "dc.welcomeQueue";

let welcomeWin = null; // module-level handle to the opened window (sender side)

function makeChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch (e) {
    return null;
  }
}

// Persist (so a freshly opened window has data on mount) then broadcast (for an
// already-open window). Shared by the open + publish paths below.
function sendQueue(message) {
  const stamped = { ...message, postedAt: Date.now() };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stamped));
  } catch (e) {
    /* ignore quota/availability errors */
  }

  const ch = makeChannel();
  if (ch) {
    ch.postMessage(stamped);
    ch.close();
  }
}

// --- Sender side (dashboard) ---

// Open or focus the welcome window and seed it with the queue. `activeId` sets
// the initial selection (an appointmentId, or null for the general screen).
export function openWelcomeQueue({ clinic, patients, activeId }) {
  sendQueue({ clinic, patients, activeId });

  // Reuse the existing window if we still hold a live handle (no reload),
  // otherwise open the fixed-name window (reuses any same-named window).
  if (welcomeWin && !welcomeWin.closed) {
    welcomeWin.focus();
    return true;
  }
  welcomeWin = window.open(WELCOME_ROUTE, WELCOME_WINDOW_NAME);
  if (welcomeWin) {
    welcomeWin.focus();
    return true;
  }
  return false; // popup blocked
}

// Live refresh of the queue without opening a window or changing selection.
// `activeId` is intentionally omitted so the receiver keeps its current pick.
export function publishWelcomeQueue({ clinic, patients }) {
  sendQueue({ clinic, patients });
}

// --- Receiver side (welcome board) ---
export function readLastQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function subscribeWelcomeQueue(callback) {
  let lastStamp = 0;
  const cleanups = [];

  const deliver = (message) => {
    if (!message) return;
    const stamp = message.postedAt || 0;
    if (stamp && stamp <= lastStamp) return; // de-dupe channel + storage double-fire
    lastStamp = stamp || lastStamp;
    callback(message);
  };

  const ch = makeChannel();
  if (ch) {
    ch.onmessage = (e) => deliver(e.data);
    cleanups.push(() => ch.close());
  }

  const onStorage = (e) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        deliver(JSON.parse(e.newValue));
      } catch (err) {
        /* ignore */
      }
    }
  };
  window.addEventListener("storage", onStorage);
  cleanups.push(() => window.removeEventListener("storage", onStorage));

  return () => cleanups.forEach((fn) => fn());
}
